<script setup lang="ts">
import { computed, ref, watch, type PropType } from 'vue';
import type { SelectOption } from 'naive-ui';
import { CommentingOptionsIncludeAgents } from '@/api';
import GaiaBanner from '../composite/GaiaBanner.vue';
import GaiaButton from '../base/GaiaButton.vue';
import GaiaFormField from '../base/GaiaFormField.vue';
import GaiaModal from '../base/GaiaModal.vue';
import GaiaSelect from '../base/selects/GaiaSelect.vue';
import GaiaText from '../base/GaiaText.vue';

export type ReviewExportType = 'summary' | 'data' | 'commented-word';
export type ReviewExportFormat = 'pdf' | 'markdown' | 'csv' | 'xlsx';

export interface ReviewExportRequest {
  type: ReviewExportType;
  format: ReviewExportFormat;
  columns: string[];
  includeAgents: CommentingOptionsIncludeAgents[];
  allowedAnswers: string[];
}

/**
 * The review detail's export dialog: a summary as PDF or Markdown, the field
 * data as CSV or XLSX, or the source Word file with the AI's comments in it.
 *
 * Only the form lives here — the view keeps the export services, so the
 * generators stay testable without a dialog around them.
 */

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  /** No summary generated yet — only field data can be exported. */
  hasSummary: {
    type: Boolean,
    default: false,
  },
  /** Commented-Word export needs a .docx source. */
  isWordDocument: {
    type: Boolean,
    default: false,
  },
  hasSuggestions: {
    type: Boolean,
    default: false,
  },
  /** The evaluation answers present on this review, for the risk-class filter. */
  availableEvaluationAnswers: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
  columnOptions: {
    type: Array as PropType<SelectOption[]>,
    default: () => [],
  },
  /** Columns pre-selected when the dialog opens. */
  defaultColumns: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
  exporting: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  'update:show': [value: boolean];
  export: [request: ReviewExportRequest];
}>();

const exportType = ref<ReviewExportType>('summary');
const exportFormat = ref<ReviewExportFormat>('pdf');
const selectedColumns = ref<string[]>([...props.defaultColumns]);
const includeAgents = ref<CommentingOptionsIncludeAgents[]>([]);
const allowedAnswers = ref<string[]>([]);

// `REVIEW` is the evaluation agent — the enum member and the product word for
// it disagree, so the label follows the product.
const agentOptions = computed<SelectOption[]>(() => {
  const options: SelectOption[] = [
    { label: 'Extraction', value: CommentingOptionsIncludeAgents.EXTRACTION },
    { label: 'Evaluation', value: CommentingOptionsIncludeAgents.REVIEW },
  ];
  if (props.hasSuggestions) {
    options.push({ label: 'Suggestion', value: CommentingOptionsIncludeAgents.SUGGESTION });
  }
  return options;
});

/** Suggestions are the point of the commented export when there are any. */
function defaultAgents(): CommentingOptionsIncludeAgents[] {
  return props.hasSuggestions ? [CommentingOptionsIncludeAgents.SUGGESTION] : [];
}

const typeOptions = computed<SelectOption[]>(() => {
  const options: SelectOption[] = [];
  if (props.hasSummary) options.push({ label: 'Summary', value: 'summary' });
  options.push({ label: 'Data', value: 'data' });
  if (props.isWordDocument) options.push({ label: 'Commented Word', value: 'commented-word' });
  return options;
});

const formatOptions = computed<SelectOption[]>(() =>
  exportType.value === 'summary' && props.hasSummary
    ? [
        { label: 'PDF', value: 'pdf' },
        { label: 'Markdown', value: 'markdown' },
      ]
    : [
        { label: 'CSV', value: 'csv' },
        { label: 'Excel (XLSX)', value: 'xlsx' },
      ]
);

/** The risk-class filter only bites on the two agents that carry evaluations. */
const showAnswerFilter = computed(
  () =>
    exportType.value === 'commented-word' &&
    props.availableEvaluationAnswers.length > 0 &&
    (includeAgents.value.includes(CommentingOptionsIncludeAgents.EXTRACTION) ||
      includeAgents.value.includes(CommentingOptionsIncludeAgents.REVIEW))
);

const answerOptions = computed<SelectOption[]>(() =>
  props.availableEvaluationAnswers.map(answer => ({ label: answer, value: answer }))
);

// Reset to a shape the review can actually produce each time it opens: without
// a summary, "Summary" is not on offer at all.
watch(
  () => props.show,
  show => {
    if (!show) return;
    exportType.value = props.hasSummary ? 'summary' : 'data';
    exportFormat.value = props.hasSummary ? 'pdf' : 'csv';
    selectedColumns.value = [...props.defaultColumns];
    includeAgents.value = defaultAgents();
    allowedAnswers.value = [];
  }
);

watch(exportType, type => {
  exportFormat.value = type === 'data' ? 'csv' : 'pdf';
});

function submit(): void {
  emit('export', {
    type: exportType.value,
    format: exportFormat.value,
    columns: selectedColumns.value,
    includeAgents: includeAgents.value,
    allowedAnswers: allowedAnswers.value,
  });
}
</script>

<template>
  <gaia-modal
    :show="props.show"
    title="Export review"
    width="600px"
    :mask-closable="false"
    @update:show="emit('update:show', $event)"
  >
    <n-flex vertical :size="16" :wrap="false">
      <gaia-banner v-if="!props.hasSummary" type="info" plain>
        No summary available yet. Only field data can be exported.
      </gaia-banner>

      <gaia-form-field label="Export type">
        <gaia-select
          v-model:value="exportType"
          :options="typeOptions"
          placeholder="Select export type"
        />
      </gaia-form-field>

      <gaia-form-field v-if="exportType !== 'commented-word'" label="Format">
        <gaia-select
          v-model:value="exportFormat"
          :options="formatOptions"
          placeholder="Select format"
        />
      </gaia-form-field>

      <gaia-form-field
        v-if="exportType === 'data'"
        label="Columns to include"
        hint="Drag to reorder."
      >
        <!-- Raw naive: nothing wraps n-transfer, and a two-list picker with
             reordering has no Gaia equivalent to compose from. -->
        <n-transfer
          v-model:value="selectedColumns"
          :options="props.columnOptions"
          :style="{ width: '100%', height: '300px' }"
        />
      </gaia-form-field>

      <template v-if="exportType === 'commented-word'">
        <gaia-form-field
          label="Include agents"
          hint="Leave empty to include every agent's comments."
        >
          <gaia-select
            v-model:value="includeAgents"
            multiple
            clearable
            :options="agentOptions"
            placeholder="Select which agents to include"
          />
        </gaia-form-field>

        <gaia-form-field
          v-if="showAnswerFilter"
          label="Risk class filter"
          hint="Leave empty to include every evaluation."
        >
          <gaia-select
            v-model:value="allowedAnswers"
            multiple
            clearable
            :options="answerOptions"
            placeholder="Select risk classes to include"
          />
        </gaia-form-field>

        <gaia-text v-if="!props.hasSuggestions" size="meta" tone="subtle">
          This review has no drafted suggestions, so the comments will carry findings only.
        </gaia-text>
      </template>
    </n-flex>

    <template #action>
      <gaia-button @click="emit('update:show', false)">Cancel</gaia-button>
      <gaia-button
        type="primary"
        :loading="props.exporting"
        :disabled="props.exporting || (exportType === 'commented-word' && !props.isWordDocument)"
        @click="submit"
      >
        {{ props.exporting ? 'Exporting…' : 'Export' }}
      </gaia-button>
    </template>
  </gaia-modal>
</template>
